import { NavLink } from 'react-router-dom';
import classNames from 'classnames/bind';
import styles from './Navigation.module.css';

import {
  USER_GUIDE,
  USER_GUIDE_URL,
  NAVIGATION_MENU_MAP,
} from '../../constants';

import Icon from '../Icon';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { menuChanged } from '../../features/infoSlice';
import Button from '../Button';
import { isMobile } from '../../utils/util';
import { useSlice } from '../../hooks/useSlice';
import { Options } from '../../types/library';
import {
  useAddMenuHistoryMutation,
  useGetMenuListQuery,
} from '../../services/main';
import { menuListAdded } from '../../features/mainSlice';
import { useEffect } from 'react';

interface Navigation {
  toggleMenu: () => void;
  isHamburgerMenuOpen?: boolean;
}

export default function Navigation({
  toggleMenu,
  isHamburgerMenuOpen: isHamburgerMenuOpen,
}: Navigation) {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const { selectOptions } = useSlice();
  const { isEditMode }: Options = useAppSelector(selectOptions);

  const { data: menuList, isLoading } = useGetMenuListQuery();
  const [addMenuHistory] = useAddMenuHistoryMutation();

  const navigateToPage = async (
    e: React.MouseEvent,
    key: string,
    cd: string
  ) => {
    if (key === 'userGuide') {
      e.preventDefault();
      window.open(USER_GUIDE_URL, '_blank');
    } else {
      dispatch(menuChanged(key));
      isHamburgerMenuOpen && toggleMenu();
      try {
        await addMenuHistory({ cd });
      } catch (error) {
        console.error('Failed to Add Menu History', error);
      }
    }
  };

  useEffect(() => {
    menuList && dispatch(menuListAdded(menuList));
  }, [menuList]);

  if (isLoading || !menuList) return null;

  const sortedMenuList = Object.values(NAVIGATION_MENU_MAP)
    .map((menuName) => menuList.find((menu) => menu.nm === menuName))
    .filter((menu) => menu !== undefined);

  const navigationMenuList = [...sortedMenuList, { cd: '', nm: USER_GUIDE }];
  const navKeyList = [
    'library',
    'share',
    'search',
    'mydrive',
    'trash',
    'classroom',
    'settings',
    'userGuide',
  ];

  return (
    <nav>
      {
        <div className={cx('nav_area', { 'layer--open': isHamburgerMenuOpen })}>
          <ul>
            {navigationMenuList.map(({ cd, nm }, i) => {
              const key = navKeyList[i];
              return (
                <li key={cd} title={nm}>
                  <NavLink
                    to={key}
                    className={({ isActive }) =>
                      cx('btn', `nav_${key}`, { on: isActive })
                    }
                    onClick={(e) => navigateToPage(e, key, cd)}
                  >
                    <Icon name={key} />
                    <span>{nm}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      }
      {isMobile && isEditMode ? null : (
        <Button
          name="menu"
          classnames={[
            'open_minimal_nav',
            isHamburgerMenuOpen ? 'layer--open' : '',
          ]}
          isBlindName={true}
          handleClick={toggleMenu}
        >
          <i
            className={cx('svg_i', { 'layer--open': isHamburgerMenuOpen })}
          ></i>
        </Button>
      )}
    </nav>
  );
}
